import { useRef, useEffect } from "react";
import { Modal } from "src/components/modal";
import { HiMap } from "react-icons/hi2";
import * as atlas from "azure-maps-control";
import { useRateCalculator } from "../context/rate-calculator-state";

import { AzureKeyCredential } from "@azure/core-auth";
import MapsSearch, { isUnexpected } from "@azure-rest/maps-search";

export function MapButton() {
    const getModal = (id: string) => {
        return document.getElementById(id) as HTMLDialogElement;
    };
    const openModal = async (id: string) => {
        getModal(id).showModal();
    };
    const closeModal = async (id: string) => {
        getModal(id).close();
    };

    //State Variables
    const rateCalculatorState = useRateCalculator();

    // Map Search
    // https://learn.microsoft.com/en-us/javascript/api/overview/azure/maps-search-rest-readme?view=azure-node-preview#request-latitude-and-longitude-coordinates-for-an-address
    const subscriptionKey = "2AOAX1DVcq5C809hqDSmyPU3gQ0wyvW8gNZu21lxwhQ";
    const credential = new AzureKeyCredential(subscriptionKey);
    const client = MapsSearch(credential);
    const mapRef = useRef<atlas.Map | null>(null);

    async function getCoords(address: string) {
        var response = await client
            .path("/geocode")
            .get({ queryParameters: { query: address } });

        if (isUnexpected(response)) {
            throw response.body.error;
        }

        if (!response.body.features) {
            console.log(`No coordinates found for the address!`);
        } else {
            var [lon, lat] = response.body.features[0].geometry.coordinates;
            return [lon, lat];
        }
    }

    useEffect(() => {
        function initializeMap() {
            console.log("Initializing map...");
            var map = new atlas.Map("myMap", {
                language: "en-US",
                authOptions: {
                    authType: atlas.AuthenticationType.subscriptionKey,
                    subscriptionKey: subscriptionKey,
                },
            });

            map.events.add("ready", () => {
                addDataSources();
            });

            mapRef.current = map;
        }

        async function addDataSources() {
            var map = mapRef.current;
            var dataSource = new atlas.source.DataSource();
            map?.sources.add(dataSource);

            // Get coordinates of origin and destination
            var formData = rateCalculatorState.currentFormData;
            var origin = await getCoords(`${formData.OriginZip}, US`);
            var destination = await getCoords(`${formData.DestinationZip}, US`);

            if (origin == null || destination == null) return; // Missing location data, bail early.

            //Create a line and add it to the data source.
            dataSource.add(
                new atlas.data.LineString([
                    new atlas.data.Position(origin[0], origin[1]),
                    new atlas.data.Position(destination[0], destination[1]),
                ]),
            );

            //Create a line layer to render the line to the map.
            map?.layers.add(
                new atlas.layer.LineLayer(dataSource, undefined, {
                    strokeColor: "#ac2226",
                    strokeWidth: 5,
                    strokeDashArray: [3, 2],
                }),
            );

            /*Add point locations*/
            var positions = [origin, destination];

            /*Add the points to the data source*/
            dataSource.add(
                positions.map((p) => new atlas.data.Point([p[0], p[1]])),
            );

            //Create a bubble layer to render the filled in area of the circle, and add it to the map.*/
            map?.layers.add(
                new atlas.layer.BubbleLayer(dataSource, undefined, {
                    radius: 3,
                    strokeColor: "#ac2226",
                    strokeWidth: 5,
                    color: "White",
                }),
            );

            map?.setCamera({
                bounds: atlas.data.BoundingBox.fromPositions(positions),
                padding: 20,
            });
        }

        initializeMap();
    }, []);

    return (
        <>
            <button
                className="btn btn-xs btn-outline flex justify-start"
                type="button"
                onClick={() => openModal("MapModal")}
            >
                <span className="flex gap-2">
                    <HiMap className="w-4 h-4" />
                    View Map
                </span>
            </button>

            <Modal id="MapModal" style="overflow-hidden max-w-163">
                <div className="h-full w-full flex flex-col gap-2 items-center">
                    <div className="h-150 w-150 bg-gray-200">
                        <div id="myMap" className="h-full w-full"></div>
                    </div>

                    <button
                        type="reset"
                        className="btn btn-link mt-2"
                        onClick={() => closeModal("MapModal")}
                    >
                        Close
                    </button>
                </div>
            </Modal>
        </>
    );
}
